import React from "react";
import { Box, Flex, Text, VStack, useColorModeValue } from "@chakra-ui/react";

const RADAR_KEYFRAMES = `
@keyframes payinapo-radar-spin {
  to { transform: rotate(360deg); }
}
@keyframes payinapo-radar-ping {
  0% { transform: scale(0.88); opacity: 0.55; }
  100% { transform: scale(1.45); opacity: 0; }
}
@keyframes payinapo-blip {
  0%, 100% { opacity: 0.2; transform: scale(0.85); }
  50% { opacity: 1; transform: scale(1.15); }
}
`;

/**
 * Full-page–style radar scanner while recommendations or a profile load.
 * Matches app glass / brand accent (lime–mint) treatment.
 */
export default function ProfilesLoadingRadar({
  title = "Scanning for people near you",
  subtitle = "Sweeping your area for matches. This usually takes just a moment.",
  minH = "56vh",
}) {
  const shellBg = useColorModeValue(
    "linear-gradient(165deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.98) 100%)",
    "linear-gradient(165deg, rgba(22,22,24,0.92) 0%, rgba(10,10,11,0.98) 100%)"
  );
  const shellBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const diskBg = useColorModeValue(
    "radial-gradient(circle at 50% 45%, rgba(255,255,255,0.9) 0%, rgba(226,232,240,0.55) 42%, rgba(203,213,225,0.35) 100%)",
    "radial-gradient(circle at 50% 45%, rgba(52,211,153,0.08) 0%, rgba(18,18,20,0.92) 48%, rgba(5,5,6,0.98) 100%)"
  );
  const ringColor = useColorModeValue("rgba(34,197,94,0.35)", "rgba(52,211,153,0.32)");
  const sweepInner = useColorModeValue(
    "conic-gradient(from 0deg at 50% 50%, transparent 0deg 298deg, rgba(74,222,128,0.55) 298deg 338deg, rgba(190,242,100,0.35) 338deg 352deg, transparent 352deg 360deg)",
    "conic-gradient(from 0deg at 50% 50%, transparent 0deg 298deg, rgba(52,211,153,0.55) 298deg 338deg, rgba(253,224,71,0.22) 338deg 352deg, transparent 352deg 360deg)"
  );
  const hubGradient = useColorModeValue(
    "linear(to-br, brand.400, brand.600)",
    "linear(to-br, brand.300, brand.600)"
  );
  const titleColor = useColorModeValue("gray.900", "white");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const gridColor = useColorModeValue("rgba(15,23,42,0.06)", "rgba(255,255,255,0.04)");
  const shellShadow = useColorModeValue(
    "0 12px 40px rgba(15,23,42,0.08)",
    "0 20px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)"
  );
  const diskBorder = useColorModeValue("gray.200", "whiteAlpha.150");
  const diskInsetShadow = useColorModeValue(
    "inset 0 0 24px rgba(255,255,255,0.5)",
    "inset 0 0 32px rgba(0,0,0,0.35)"
  );

  const blips = [
    { top: "18%", left: "62%", delay: "0s" },
    { top: "52%", left: "78%", delay: "0.4s" },
    { top: "72%", left: "38%", delay: "0.8s" },
    { top: "34%", left: "22%", delay: "1.2s" },
  ];

  return (
    <>
      <style>{RADAR_KEYFRAMES}</style>
      <Flex
        direction="column"
        align="center"
        justify="center"
        w="100%"
        minH={minH}
        py={{ base: 10, md: 14 }}
        px={4}
      >
        <Box
          w="100%"
          maxW="md"
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={shellBorder}
          bg={shellBg}
          backdropFilter="blur(12px)"
          boxShadow={shellShadow}
          py={{ base: 8, md: 10 }}
          px={6}
        >
          <VStack spacing={8} w="100%">
            <Box position="relative" w="260px" h="260px" mx="auto">
              {/* subtle coordinate grid */}
              <Box
                position="absolute"
                inset="0"
                borderRadius="full"
                opacity={0.9}
                bgImage={`repeating-linear-gradient(0deg, transparent, transparent 19px, ${gridColor} 19px, ${gridColor} 20px), repeating-linear-gradient(90deg, transparent, transparent 19px, ${gridColor} 19px, ${gridColor} 20px)`}
                pointerEvents="none"
              />

              {/* expanding rings */}
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  position="absolute"
                  left="50%"
                  top="50%"
                  w="78%"
                  h="78%"
                  ml="-39%"
                  mt="-39%"
                  borderRadius="full"
                  border="1px solid"
                  borderColor={ringColor}
                  animation="payinapo-radar-ping 2.8s ease-out infinite"
                  style={{ animationDelay: `${i * 0.75}s` }}
                  pointerEvents="none"
                />
              ))}

              {/* main radar disk */}
              <Box
                position="absolute"
                left="50%"
                top="50%"
                w="72%"
                h="72%"
                ml="-36%"
                mt="-36%"
                borderRadius="full"
                borderWidth="1px"
                borderColor={diskBorder}
                bg={diskBg}
                overflow="hidden"
                boxShadow={diskInsetShadow}
              >
                <Box
                  position="absolute"
                  left="-25%"
                  top="-25%"
                  w="150%"
                  h="150%"
                  bg={sweepInner}
                  style={{ animation: "payinapo-radar-spin 2.6s linear infinite" }}
                />
              </Box>

              {/* center hub */}
              <Flex
                position="absolute"
                left="50%"
                top="50%"
                w="18%"
                h="18%"
                ml="-9%"
                mt="-9%"
                borderRadius="full"
                bgGradient={hubGradient}
                align="center"
                justify="center"
                boxShadow="0 0 20px rgba(52,211,153,0.45), inset 0 1px 0 rgba(255,255,255,0.35)"
                borderWidth="1px"
                borderColor="whiteAlpha.400"
                zIndex={2}
              >
                <Box w="35%" h="35%" borderRadius="full" bg="whiteAlpha.900" opacity={0.9} />
              </Flex>

              {/* blips */}
              {blips.map((b, idx) => (
                <Box
                  key={idx}
                  position="absolute"
                  w="10px"
                  h="10px"
                  borderRadius="full"
                  bg="brand.400"
                  boxShadow="0 0 12px rgba(52,211,153,0.85)"
                  top={b.top}
                  left={b.left}
                  zIndex={1}
                  style={{
                    animation: "payinapo-blip 1.8s ease-in-out infinite",
                    animationDelay: b.delay,
                  }}
                />
              ))}
            </Box>

            <VStack spacing={2} textAlign="center" px={1}>
              <Text fontSize="lg" fontWeight="semibold" color={titleColor} letterSpacing="-0.02em">
                {title}
              </Text>
              <Text fontSize="sm" color={muted} lineHeight="tall" maxW="sm">
                {subtitle}
              </Text>
            </VStack>
          </VStack>
        </Box>
      </Flex>
    </>
  );
}
